'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { getCookie } from '@/app/lib/utils'
import { NOT_AUTHENTICATED, SESSION_STATE_COOKIE, USER_NAME_COOKIE } from '@/app/lib/constants'

interface SessionContextValue {
  /** `true` si el usuario tiene sesión iniciada frente al sistema de identificación central. */
  isLoggedIn: boolean
  /** Nombre del usuario con sesión iniciada (`readUserName`), o `null`. */
  userName: string | null
  /**
   * Actualiza el estado de sesión conocido por el cliente (usado por
   * `SessionChecker`); el nombre se relee a la vez de su cookie.
   */
  setIsLoggedIn: (isLoggedIn: boolean) => void
}

// `null` por defecto: usar el hook fuera del provider es un error de
// programación y debe fallar de forma explícita, no devolver un valor silencioso.
const SessionContext = createContext<SessionContextValue | null>(null)

/** Lee la cookie `session_state` en cliente. Compartida con `SessionChecker`. */
export function readIsLoggedIn(): boolean {
  const value = getCookie(SESSION_STATE_COOKIE)
  return Boolean(value) && value !== NOT_AUTHENTICATED
}

/**
 * Lee el nombre del usuario de la cookie `user_name` (ya decodificada por
 * `getCookie`): los `+` de la codificación de formulario pasan a espacios y
 * se colapsan los repetidos; vacía o ausente ⇒ `null`.
 */
export function readUserName(): string | null {
  const name = getCookie(USER_NAME_COOKIE)?.replace(/\+/g, ' ').replace(/\s+/g, ' ').trim()
  return name ? name : null
}

interface SessionState {
  readonly isLoggedIn: boolean
  readonly userName: string | null
}

/** Estado de sesión con el nombre releído de su cookie solo si hay sesión. */
function toSessionState(isLoggedIn: boolean): SessionState {
  return { isLoggedIn, userName: isLoggedIn ? readUserName() : null }
}

interface SessionProviderProps {
  children: ReactNode
  /**
   * Override explícito del valor inicial (uso en tests). En producción no se
   * pasa: el servidor y el primer render de cliente siempre arrancan en
   * `false` (sin depender de la cookie `session_state`, para no sacar a la
   * página del renderizado estático — ver
   * specs/010-mejora-rendimiento-web/research.md §2), y un efecto posterior
   * al montaje relee la cookie real con `readIsLoggedIn()` y ajusta el
   * estado si difiere.
   */
  initialIsLoggedIn?: boolean
  /** Override del nombre inicial (uso en tests), igual que `initialIsLoggedIn`. */
  initialUserName?: string | null
}

export function SessionProvider({
  children,
  initialIsLoggedIn = false,
  initialUserName = null,
}: SessionProviderProps) {
  const [session, setSession] = useState<SessionState>({
    isLoggedIn: initialIsLoggedIn,
    userName: initialUserName,
  })

  useEffect(() => {
    // Solo ajusta si la cookie existe de verdad — su ausencia no debe pisar
    // un valor explícito (uso en tests) ni el valor por defecto. No se puede
    // resolver durante el render: leer `document.cookie` ahí correría también
    // en el primer render de cliente (el de la hidratación) y podría no
    // coincidir con el HTML del servidor, rompiendo la hidratación.
    if (getCookie(SESSION_STATE_COOKIE) === undefined) return
    const real = toSessionState(readIsLoggedIn())
    // eslint-disable-next-line react-hooks/set-state-in-effect -- lectura de cookie post-hidratación, ver comentario arriba
    setSession((current) =>
      current.isLoggedIn === real.isLoggedIn && current.userName === real.userName ? current : real,
    )
  }, [])

  const setIsLoggedIn = useCallback((isLoggedIn: boolean) => {
    setSession(toSessionState(isLoggedIn))
  }, [])

  const value = useMemo(
    () => ({ isLoggedIn: session.isLoggedIn, userName: session.userName, setIsLoggedIn }),
    [session, setIsLoggedIn],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSessionContext(): SessionContextValue {
  const ctx = useContext(SessionContext)
  if (!ctx) {
    throw new Error('useSessionContext debe usarse dentro de <SessionProvider>.')
  }
  return ctx
}
